import React, { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, User, Building2, Calendar, Edit, Trash2, Clock, XCircle, ChevronDown, History } from "lucide-react";
import { format } from "date-fns";
import api from "../../api/api";
import ActivityLogModal from "../common/ActivityLogModal";
import "../../css/deals/dealscard.css";

const STAGE_CLASS = {
  approval:        "stage-approval",
  negotiation:     "stage-negotiation",
  documentation:   "stage-documentation",
  payment_pending: "stage-payment",
  closed_won:      "stage-won",
  closed_lost:     "stage-lost",
};

const STAGE_LABEL = {
  approval:        "Pending Approval",
  negotiation:     "Negotiation",
  documentation:   "Documentation",
  payment_pending: "Payment Pending",
  closed_won:      "Closed Won",
  closed_lost:     "Closed Lost",
};

const STAGE_OPTIONS = [
  { value: "negotiation",     label: "Negotiation" },
  { value: "documentation",   label: "Documentation" },
  { value: "payment_pending", label: "Payment Pending" },
  { value: "closed_won",      label: "Closed Won" },
  { value: "closed_lost",     label: "Closed Lost" },
];

function formatINR(val) {
  const n = typeof val === "string"
    ? parseFloat(String(val).replace(/[^0-9.]/g, ""))
    : Number(val);
  if (!n || isNaN(n)) return "—";
  if (n >= 10_000_000) return `₹${parseFloat((n / 10_000_000).toFixed(2))} Cr`;
  if (n >= 100_000)    return `₹${parseFloat((n / 100_000).toFixed(2))} L`;
  if (n >= 1_000)      return `₹${parseFloat((n / 1_000).toFixed(1))} K`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function fullINR(val) {
  const n = Number(val);
  return n ? `₹${n.toLocaleString("en-IN")}` : "";
}

export default function DealCard({ deal, index, onEdit, onDelete, onApprove, isAdmin, onStageUpdate }) {
  const [stageDropdownOpen, setStageDropdownOpen] = useState(false);
  const [updatingStage, setUpdatingStage] = useState(false);
  const [showLog, setShowLog] = useState(false);

  const stageClass = STAGE_CLASS[deal.stage] || "stage-negotiation";
  const stageLabel = STAGE_LABEL[deal.stage] || deal.stage?.replace(/_/g, " ");

  const isPendingApproval = deal.stage === "approval" && deal.approvalStatus === "pending";
  const isRejected        = deal.approvalStatus === "rejected";

  // Handle stage change
  const handleStageChange = async (newStage) => {
    if (!isAdmin || newStage === deal.stage) {
      setStageDropdownOpen(false);
      return;
    }

    setUpdatingStage(true);
    try {
      const response = await api.patch(`/deals/${deal._id}/stage`, { stage: newStage });
      setStageDropdownOpen(false);
      // Notify parent to refresh data
      if (onStageUpdate) {
        onStageUpdate(response.data.deal);
      }
    } catch (error) {
      console.error("Failed to update stage:", error);
      alert("Failed to update deal stage: " + (error.response?.data?.message || error.message));
    } finally {
      setUpdatingStage(false);
    }
  };

  return (
    <>
    <motion.div
      className="deal-motion"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.3) }}
    >
      <div className="deal-card">

        {/* ── Header ── */}
        <div className="deal-header">
          <div className="deal-title">
            <div className="deal-icon">
              <TrendingUp size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h3>{deal.dealName}</h3>

              {/* Stage Badge with Dropdown for Managers */}
              {isAdmin && deal.stage !== "approval" ? (
                <div className="stage-dropdown-wrapper">
                  <button
                    className={`deal-badge ${stageClass} stage-dropdown-trigger`}
                    onClick={() => setStageDropdownOpen(!stageDropdownOpen)}
                    title="Click to change stage"
                    disabled={updatingStage}
                  >
                    {stageLabel}
                    <ChevronDown size={12} />
                  </button>

                  {stageDropdownOpen && (
                    <div className="stage-dropdown-menu">
                      {STAGE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          className={`stage-option ${opt.value === deal.stage ? "active" : ""}`}
                          onClick={() => handleStageChange(opt.value)}
                          disabled={updatingStage}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <span className={`deal-badge ${stageClass}`}>{stageLabel}</span>
              )}
            </div>
          </div>

          <div className="deal-actions">
            {onApprove && (
              <button className="icon-btn approval-icon-btn" title="Review Approval" onClick={onApprove}>
                <Clock size={15} />
              </button>
            )}
            <button className="icon-btn" title="Activity Log" onClick={() => setShowLog(true)}>
              <History size={15} />
            </button>
            <button className="icon-btn" title="Edit" onClick={onEdit} disabled={isPendingApproval}>
              <Edit size={15} />
            </button>
            <button className="icon-btn danger" title="Delete" onClick={onDelete}>
              <Trash2 size={15} />
            </button>
          </div>
        </div>

        {/* ── Approval status banner ── */}
        {(isPendingApproval || isRejected) && (
          <div className={`approval-banner ${isPendingApproval ? "pending" : "rejected"}`}>
            {isPendingApproval ? <Clock size={14} /> : <XCircle size={14} />}
            <span>
              {isPendingApproval
                ? <>Pending manager approval • requested by <strong>{deal.createdByEmail?.split("@")[0]}</strong></>
                : <>Rejected{deal.rejectionReason ? `: ${deal.rejectionReason}` : ""}</>
              }
            </span>
          </div>
        )}

        {/* ── Content ── */}
        <div className="deal-content">

          {/* Deal value — always shown */}
          <div className="deal-value">
            <p
              className="amount"
              title={fullINR(deal.dealValue)}
            >
              {formatINR(deal.dealValue)}
            </p>
            <span
              className="subtext"
              title={deal.commissionAmout ? `Commission: ${fullINR(deal.commissionAmout)}` : ""}
            >
              {deal.commissionAmout
                ? `Commission: ${formatINR(deal.commissionAmout)}`
                : "No commission set"}
            </span>
          </div>

          {/* Win probability — shown only when available, space held otherwise */}
          <div className="probability">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${deal.winProbability || 0}%` }}
              />
            </div>
            <span>{deal.winProbability || 0}%</span>
          </div>

          {/* Info rows — always shown (with fallback dashes) */}
          <div className="deal-info">
            <div className="info-row">
              <User size={13} />
              <span>
                {deal.leadFullName || (typeof deal.leadID === "object" ? deal.leadID?.full_name : deal.leadID) || "No lead linked"}
              </span>
            </div>

            <div className="info-row">
              <Building2 size={13} />
              <span>
                {deal.propertyName || (typeof deal.propertyID === "object" ? deal.propertyID?.propertyName : deal.propertyID) || "No property linked"}
              </span>
            </div>

            <div className="info-row">
              <Calendar size={13} />
              <span>
                {deal.expectedCloseDate
                  ? `Close: ${format(new Date(deal.expectedCloseDate), "dd MMM yyyy")}`
                  : "No close date"}
              </span>
            </div>
          </div>

          {/* Notes — compact, pushed toward bottom */}
          {deal.notes && (
            <div className="deal-notes">{deal.notes}</div>
          )}

          {/* Footer — agreement + agent — always at bottom */}
          <div className="deal-footer">
            <span className={`deal-badge ${deal.agreementSigned ? "stage-won" : "stage-lost"}`}>
              {deal.agreementSigned ? "Agreement Signed" : "No Agreement"}
            </span>

            {isAdmin && deal.createdByEmail && (
              <span className="assigned-row">
                <strong>{deal.createdByEmail.split("@")[0]}</strong>
              </span>
            )}
          </div>

        </div>
      </div>
    </motion.div>

    {showLog && (
      <ActivityLogModal log={deal.activityLog || []} onClose={() => setShowLog(false)} />
    )}
    </>
  );
}
